import {
  type AgentReportCardMonth,
  type AgentReportCardMode,
  type AgentReportCardRow,
} from '@utazzo/shared';
import { UzButton, UzCard, UzSelect } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { getAgentReportCard } from '../invoices.api';
import { useAgentReportCard } from '../use-invoices';

import {
  downloadExcel,
  downloadPdf,
  type CsvColumn,
} from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';

/**
 * Agent Report Card (route /reports/agent-report-card): 1:1 rebuild of the legacy
 * agentreportcard.aspx. One row per source (or agent); each quarter cell is a
 * 4-column grid (month header + one line per metric); Current Month and YTD are
 * single stacked columns; the ALL row is followed by its UTAZZO/CORP DOM-INT
 * section. A second table repeats the same layout per product category.
 */

const ABBR: Record<number, string> = {
  1: 'JAN',
  2: 'FEB',
  3: 'MAR',
  4: 'APR',
  5: 'MAY',
  6: 'JUN',
  7: 'JUL',
  8: 'AUG',
  9: 'SEP',
  10: 'OCT',
  11: 'NOV',
  12: 'DEC',
};
// Financial-year month order (index 0 = April ... 11 = March).
const FY_MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
// Quarters as FY-month-array indices.
const QUARTERS = [
  { label: 'Q1TD', idxs: [0, 1, 2] },
  { label: 'Q2TD', idxs: [3, 4, 5] },
  { label: 'Q3TD', idxs: [6, 7, 8] },
  { label: 'Q4TD', idxs: [9, 10, 11] },
];
const YTD_IDXS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const currentFyStart = (): number => {
  const d = new Date();
  return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
};

const rupees = (paise: string): string => {
  const n = Number(paise) / 100;
  return n === 0 ? '0' : n.toFixed(2);
};
const pct = (num: bigint, den: bigint): string =>
  den === 0n ? '----' : `${((Number(num) / Number(den)) * 100).toFixed(2)}%`;
const pctAbs = (num: bigint, den: bigint): string =>
  den === 0n ? '----' : `${Math.abs((Number(num) / Number(den)) * 100).toFixed(2)}%`;

// ---- aggregation ----
interface Agg {
  sale: bigint;
  profit: bigint;
  inv: number;
  pending: number;
  void: number;
  opeff: bigint;
  pax: number;
  utzDomSale: bigint;
  utzDomProfit: bigint;
  utzDomPax: number;
  utzIntSale: bigint;
  utzIntProfit: bigint;
  utzIntPax: number;
  corpDomSale: bigint;
  corpDomProfit: bigint;
  corpDomPax: number;
  corpIntSale: bigint;
  corpIntProfit: bigint;
  corpIntPax: number;
}
const ZERO: Agg = {
  sale: 0n,
  profit: 0n,
  inv: 0,
  pending: 0,
  void: 0,
  opeff: 0n,
  pax: 0,
  utzDomSale: 0n,
  utzDomProfit: 0n,
  utzDomPax: 0,
  utzIntSale: 0n,
  utzIntProfit: 0n,
  utzIntPax: 0,
  corpDomSale: 0n,
  corpDomProfit: 0n,
  corpDomPax: 0,
  corpIntSale: 0n,
  corpIntProfit: 0n,
  corpIntPax: 0,
};
const addBundle = (a: Agg, m: AgentReportCardMonth): Agg => ({
  sale: a.sale + BigInt(m.salePaise),
  profit: a.profit + BigInt(m.profitPaise),
  inv: a.inv + m.invoiceCount,
  pending: a.pending + m.pendingCount,
  void: a.void + m.voidCount,
  opeff: a.opeff + BigInt(m.opEffPaise),
  pax: a.pax + m.pax,
  utzDomSale: a.utzDomSale + BigInt(m.utzDom.salePaise),
  utzDomProfit: a.utzDomProfit + BigInt(m.utzDom.profitPaise),
  utzDomPax: a.utzDomPax + m.utzDom.pax,
  utzIntSale: a.utzIntSale + BigInt(m.utzInt.salePaise),
  utzIntProfit: a.utzIntProfit + BigInt(m.utzInt.profitPaise),
  utzIntPax: a.utzIntPax + m.utzInt.pax,
  corpDomSale: a.corpDomSale + BigInt(m.corpDom.salePaise),
  corpDomProfit: a.corpDomProfit + BigInt(m.corpDom.profitPaise),
  corpDomPax: a.corpDomPax + m.corpDom.pax,
  corpIntSale: a.corpIntSale + BigInt(m.corpInt.salePaise),
  corpIntProfit: a.corpIntProfit + BigInt(m.corpInt.profitPaise),
  corpIntPax: a.corpIntPax + m.corpInt.pax,
});
const aggOf = (row: AgentReportCardRow, idxs: number[]): Agg =>
  idxs.reduce((acc, i) => (row.months[i] ? addBundle(acc, row.months[i]) : acc), { ...ZERO });

// ---- metrics ----
type MetricKind =
  | 'sale'
  | 'profit'
  | 'pctProfit'
  | 'pctSales'
  | 'inv'
  | 'pending'
  | 'void'
  | 'pctVoid'
  | 'opeff'
  | 'pctOpeff'
  | 'pax'
  | 'sSale'
  | 'sProfit'
  | 'sPct'
  | 'sPax';
interface Metric {
  label: string;
  kind: MetricKind;
  split?: 'utzDom' | 'utzInt' | 'corpDom' | 'corpInt';
  color?: 'red' | 'green';
}

const BASE_METRICS: Metric[] = [
  { label: 'Total Sale', kind: 'sale', color: 'red' },
  { label: 'Total Profit', kind: 'profit', color: 'green' },
  { label: 'Total Profit %', kind: 'pctProfit' },
  { label: 'Sales %', kind: 'pctSales' },
  { label: 'No. of Invoice', kind: 'inv' },
  { label: 'No. of Pending Invoice', kind: 'pending', color: 'red' },
  { label: 'No. of Void Invoice', kind: 'void' },
  { label: 'Void Invoice %', kind: 'pctVoid' },
  { label: 'Total Operation Eff.', kind: 'opeff' },
  { label: 'Operation Eff. %', kind: 'pctOpeff' },
  { label: 'PaxCount', kind: 'pax' },
];
const splitMetrics = (label: string, split: Metric['split']): Metric[] => [
  { label: `Total ${label} Sale`, kind: 'sSale', split, color: 'red' },
  { label: `Total ${label} Profit`, kind: 'sProfit', split, color: 'green' },
  { label: `Total ${label} Profit %`, kind: 'sPct', split },
  { label: `Total ${label} PaxCount`, kind: 'sPax', split },
];
const SPLIT_METRICS: Metric[] = [
  ...splitMetrics('UTAZZO DOM', 'utzDom'),
  ...splitMetrics('UTAZZO INT', 'utzInt'),
  ...splitMetrics('CORP. DOM', 'corpDom'),
  ...splitMetrics('CORP. INT', 'corpInt'),
];

const splitVals = (a: Agg, s: NonNullable<Metric['split']>) => {
  switch (s) {
    case 'utzDom':
      return { sale: a.utzDomSale, profit: a.utzDomProfit, pax: a.utzDomPax };
    case 'utzInt':
      return { sale: a.utzIntSale, profit: a.utzIntProfit, pax: a.utzIntPax };
    case 'corpDom':
      return { sale: a.corpDomSale, profit: a.corpDomProfit, pax: a.corpDomPax };
    case 'corpInt':
      return { sale: a.corpIntSale, profit: a.corpIntProfit, pax: a.corpIntPax };
  }
};
const cellValue = (metric: Metric, agg: Agg, allAgg: Agg): string => {
  if (metric.split) {
    const v = splitVals(agg, metric.split);
    switch (metric.kind) {
      case 'sSale':
        return rupees(v.sale.toString());
      case 'sProfit':
        return rupees(v.profit.toString());
      case 'sPct':
        return pct(v.profit, v.sale);
      case 'sPax':
        return String(v.pax);
      default:
        return '';
    }
  }
  switch (metric.kind) {
    case 'sale':
      return rupees(agg.sale.toString());
    case 'profit':
      return rupees(agg.profit.toString());
    case 'pctProfit':
      return pct(agg.profit, agg.sale);
    case 'pctSales':
      return pct(agg.sale, allAgg.sale);
    case 'inv':
      return String(agg.inv);
    case 'pending':
      return String(agg.pending);
    case 'void':
      return String(agg.void);
    case 'pctVoid':
      return pct(BigInt(agg.void), BigInt(agg.inv + agg.void));
    case 'opeff':
      return rupees(agg.opeff.toString());
    case 'pctOpeff':
      return pctAbs(agg.opeff, agg.sale);
    case 'pax':
      return String(agg.pax);
    default:
      return '';
  }
};

const MODE_PREFIX = 'agent:';
const colorClass = (c?: 'red' | 'green'): string =>
  c === 'red' ? 'text-red-600' : c === 'green' ? 'text-green-700' : 'text-zinc-800';
/** Consistent line height so the label column lines up with the value grids. */
const LINE = 'h-5 leading-5';
/** Fixed 4-column track for a quarter cell (month + 3 values), so wide numbers
 * never overflow into the neighbouring sub-column. */
const Q_GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 112px)',
  columnGap: 36,
};

/**
 * Props let this one pivot back two reports: the Agent Report Card (default)
 * and the legacy Monthly Sale report (retail-only, defaults to Consolidated
 * User, no product-category table) - they render the identical
 * User | CM | Q1-4TD | YTD grid.
 */
interface AgentReportCardPageProps {
  title?: string;
  description?: string;
  /** Base filename for the Excel / PDF export. */
  exportName?: string;
  /** Scope to retail (non-corporate) invoices only - the Monthly Sale base. */
  retailOnly?: boolean;
  /** Initial agent/mode selection. */
  defaultAgentSel?: string;
  /** Whether to render the product-category (Table B) pivot. */
  showCategoryTable?: boolean;
}

export const AgentReportCardPage = ({
  title = 'Agent Report Card',
  description = 'Monthly sales pivot per source / agent for one financial year, with DOM/INT and retail/corporate splits + product-category table.',
  exportName = 'agent-report-card',
  retailOnly = false,
  defaultAgentSel = 'CONSOLIDATED_LEAD',
  showCategoryTable = true,
}: AgentReportCardPageProps = {}): React.JSX.Element => {
  const [fyStartYear, setFyStartYear] = useState(currentFyStart());
  const [agentSel, setAgentSel] = useState<string>(defaultAgentSel);

  const isAgent = agentSel.startsWith(MODE_PREFIX);
  const mode: AgentReportCardMode = isAgent ? 'AGENT' : (agentSel as AgentReportCardMode);
  const agentName = isAgent ? agentSel.slice(MODE_PREFIX.length) : undefined;

  const query = {
    fyStartYear,
    mode,
    ...(agentName ? { agentName } : {}),
    ...(retailOnly ? { retailOnly: true } : {}),
  };
  const reportQuery = useAgentReportCard(query, !isAgent || Boolean(agentName));
  const data = reportQuery.data;
  const curIdx = data?.currentMonth != null ? FY_MONTHS.indexOf(data.currentMonth) : -1;
  const cmIdxs = curIdx >= 0 ? [curIdx] : [];

  const yearOptions = useMemo(() => {
    const start = currentFyStart();
    return Array.from({ length: 6 }, (_, i) => {
      const y = start - i;
      return { value: String(y), label: `${y}-${y + 1}` };
    });
  }, []);

  const agentSelectOptions = useMemo(
    () => [
      { value: 'CONSOLIDATED_LEAD', label: 'Consolidated Lead' },
      { value: 'CONSOLIDATED_USER', label: 'Consolidated User' },
      ...(data?.agents ?? []).map((n) => ({ value: `${MODE_PREFIX}${n}`, label: n })),
    ],
    [data?.agents],
  );
  const agentLabel =
    agentSelectOptions.find((o) => o.value === agentSel)?.label ?? 'Consolidated Lead';

  // ---- export (flattened) ----
  const handleExport = async (fmt: 'xlsx' | 'pdf'): Promise<void> => {
    try {
      const full = await getAgentReportCard(query);
      const cur = full.currentMonth != null ? FY_MONTHS.indexOf(full.currentMonth) : -1;
      const cmi = cur >= 0 ? [cur] : [];
      type Flat = { table: string; source: string; metric: string; vals: string[] };
      const valCols: { header: string; idxs: number[] }[] = [
        { header: 'Current Month', idxs: cmi },
        ...QUARTERS.flatMap((q) => [
          { header: ABBR[FY_MONTHS[q.idxs[0]!]!]!, idxs: [q.idxs[0]!] },
          { header: ABBR[FY_MONTHS[q.idxs[1]!]!]!, idxs: [q.idxs[1]!] },
          { header: ABBR[FY_MONTHS[q.idxs[2]!]!]!, idxs: [q.idxs[2]!] },
          { header: `${q.label} Total`, idxs: q.idxs },
        ]),
        { header: 'YTD', idxs: YTD_IDXS },
      ];
      const cols: CsvColumn<Flat>[] = [
        { header: 'Table', value: (r) => r.table },
        { header: 'Source', value: (r) => r.source },
        { header: 'Metric', value: (r) => r.metric },
        ...valCols.map((c, i) => ({ header: c.header, value: (r: Flat) => r.vals[i] ?? '' })),
      ];
      const flat: Flat[] = [];
      const pushBlock = (table: string, rows: AgentReportCardRow[]) => {
        const allRow = rows.find((x) => x.isGrandTotal);
        for (const row of rows) {
          const metrics = row.isGrandTotal ? [...BASE_METRICS, ...SPLIT_METRICS] : BASE_METRICS;
          for (const m of metrics) {
            flat.push({
              table,
              source: row.dimension,
              metric: m.label,
              vals: valCols.map((c) =>
                cellValue(m, aggOf(row, c.idxs), allRow ? aggOf(allRow, c.idxs) : ZERO),
              ),
            });
          }
        }
      };
      pushBlock('Source', full.rows);
      if (showCategoryTable) pushBlock('Category', full.categoryRows);
      const name = `${exportName}-${full.fyLabel}`;
      if (fmt === 'pdf') downloadPdf(flat, cols, name, `${title} ${full.fyLabel}`);
      else downloadExcel(flat, cols, name);
    } catch {
      toast.error('Failed to export');
    }
  };

  // ---- rendering ----
  const headerRow = (first: string): React.JSX.Element => (
    <tr className="bg-[#4a9aa8] text-center text-sm font-bold text-white">
      <th
        className="sticky left-0 z-20 border border-zinc-300 bg-[#4a9aa8] px-2 py-2 text-left"
        style={{ width: 160 }}
      >
        {first}
      </th>
      <th className="border border-zinc-300 px-2 py-2">Current Month</th>
      {QUARTERS.map((q) => (
        <th key={q.label} className="border border-zinc-300 px-2 py-2" style={{ width: 300 }}>
          {q.label}
        </th>
      ))}
      <th className="border border-zinc-300 px-2 py-2">YTD</th>
    </tr>
  );

  /** One source block = one <tr> with 7 cells (source, CM, 4 quarters, YTD). */
  const block = (
    row: AgentReportCardRow,
    allRow: AgentReportCardRow | undefined,
    metrics: Metric[],
    showName: boolean,
  ): React.JSX.Element => {
    const all = (idxs: number[]): Agg => (allRow ? aggOf(allRow, idxs) : ZERO);
    const bg = row.isGrandTotal ? 'bg-zinc-100' : 'bg-white';
    // One source of truth for per-cell padding so every column in a row matches.
    const CELL = 'border border-zinc-300 px-2 py-3';
    return (
      <tr className={`align-top text-sm ${bg}`}>
        {/* Source name + metric labels (sticky on horizontal scroll) */}
        <td className={`sticky left-0 z-10 ${CELL} ${bg}`} style={{ width: 160 }}>
          <div className="flex justify-between gap-1">
            <div className="text-[13px] font-bold text-zinc-900">
              {showName ? row.dimension : ''}
            </div>
            <div className="text-right text-[13px] text-zinc-600">
              <div className={LINE}>&nbsp;</div>
              {metrics.map((m) => (
                <div key={m.label} className={`${LINE} ${colorClass(m.color)}`}>
                  {m.label}:
                </div>
              ))}
            </div>
          </div>
        </td>
        {/* Current Month (single column) */}
        <td className={`${CELL} text-center tabular-nums`}>
          <div className={LINE}>&nbsp;</div>
          {metrics.map((m) => (
            <div key={m.label} className={`${LINE} ${colorClass(m.color)}`}>
              {cellValue(m, aggOf(row, cmIdxs), all(cmIdxs))}
            </div>
          ))}
        </td>
        {/* Quarters (4-column grids) */}
        {QUARTERS.map((q) => {
          const cells: number[][] = [[q.idxs[0]!], [q.idxs[1]!], [q.idxs[2]!], q.idxs];
          return (
            <td key={q.label} className={`${CELL} tabular-nums`}>
              <div className="text-center font-bold text-zinc-700" style={Q_GRID}>
                <div className={LINE}>{ABBR[FY_MONTHS[q.idxs[0]!]!]}</div>
                <div className={LINE}>{ABBR[FY_MONTHS[q.idxs[1]!]!]}</div>
                <div className={LINE}>{ABBR[FY_MONTHS[q.idxs[2]!]!]}</div>
                <div className={LINE}>Total</div>
              </div>
              {metrics.map((m) => (
                <div key={m.label} className={`text-center ${colorClass(m.color)}`} style={Q_GRID}>
                  {cells.map((idxs, k) => (
                    <div key={k} className={LINE}>
                      {cellValue(m, aggOf(row, idxs), all(idxs))}
                    </div>
                  ))}
                </div>
              ))}
            </td>
          );
        })}
        {/* YTD (single column) */}
        <td className={`${CELL} text-right tabular-nums`}>
          <div className={LINE}>&nbsp;</div>
          {metrics.map((m) => (
            <div key={m.label} className={`${LINE} ${colorClass(m.color)}`}>
              {cellValue(m, aggOf(row, YTD_IDXS), all(YTD_IDXS))}
            </div>
          ))}
        </td>
      </tr>
    );
  };

  const renderTable = (
    rows: AgentReportCardRow[],
    firstHeader: string,
    withSplits: boolean,
  ): React.JSX.Element => {
    const allRow = rows.find((x) => x.isGrandTotal);
    return (
      <div className="overflow-x-auto">
        <table className="border-collapse whitespace-nowrap">
          <thead>{headerRow(firstHeader)}</thead>
          <tbody>
            {rows.map((row) => (
              <React.Fragment key={row.dimension}>
                {block(row, allRow, BASE_METRICS, true)}
                {withSplits && row.isGrandTotal && block(row, allRow, SPLIT_METRICS, false)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <PageShell
      title={title}
      description={description}
      actions={
        <div className="flex items-center gap-2">
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={!data}
            onClick={() => void handleExport('pdf')}
          >
            PDF
          </UzButton>
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={!data}
            onClick={() => void handleExport('xlsx')}
          >
            Excel
          </UzButton>
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <UzSelect
          label="Year"
          containerClassName="w-40"
          value={String(fyStartYear)}
          onValueChange={(v) => setFyStartYear(Number(v))}
          options={yearOptions}
        />
        <UzSelect
          label="Agent"
          containerClassName="w-64"
          value={agentSel}
          onValueChange={setAgentSel}
          options={agentSelectOptions}
        />
        <UzButton variant="outline" onClick={() => void reportQuery.refetch()}>
          Find Report
        </UzButton>
      </div>

      <div className="mb-3 bg-cyan-200 py-1 text-center text-lg font-bold tracking-wide text-red-600 uppercase">
        {agentLabel}
      </div>

      <UzCard padding="none">
        {reportQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading report...</p>
        ) : reportQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the report.</p>
        ) : !data || data.rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">No data for this year.</p>
        ) : (
          <div className="space-y-6 p-2">
            {renderTable(data.rows, 'Source', true)}
            {showCategoryTable && renderTable(data.categoryRows, 'Category', true)}
          </div>
        )}
      </UzCard>

      <p className="mt-3 text-sm text-zinc-400">
        Sale = pre-GST sale; Profit = margin (sale - supplier cost); cancelled invoices are
        excluded. Operation Eff. = margin drift vs first-recorded profit. Figures reflect the last
        data sync.
      </p>
    </PageShell>
  );
};
